import { CommonModule } from '@angular/common';
import { Component, inject, signal, WritableSignal } from '@angular/core';
import {
    BackgroundColorDirective,
    BorderColorDirective,
    ColorDirective,
    ToggleSwitchDirective,
    TypographyDirective,
} from '@dartcounter/ui';
import { AlertService } from '@dc-core/dc-services/alert.service';
import { PreferenceService } from '@dc-core/dc-services/preference/preference.service';
import { TranslateModule } from '@ngx-translate/core';
import { JanusStateService } from 'src/app/services/janus/janus-state.service';
import { OldIconComponent } from 'src/app/shared/components/icon/icon.component';
import { FullscreenDialogLayoutComponent } from 'src/app/shared/dialogs/fullscreen-dialog-layout/fullscreen-dialog-layout.component';
import { ModalService } from 'src/app/shared/services/modal.service';

import { CountryFlagPipe } from '../../../../shared/pipes/country-flag.pipe';
import { SpeechToScoreService } from '../../services/speech-to-score.service';
import { CommandOption, CommandType } from '../../types/speech/commands';
import { RecognitionLang } from '../../types/speech-to-score.definitions';

@Component({
    selector: 'app-speech-to-score-info-dialog',
    templateUrl: 'speech-to-score-info.dialog.html',
    styleUrls: ['speech-to-score-info.dialog.css'],
    standalone: true,
    imports: [
        CommonModule,
        TranslateModule,
        OldIconComponent,
        CountryFlagPipe,
        FullscreenDialogLayoutComponent,
        TypographyDirective,
        ColorDirective,
        BackgroundColorDirective,
        BorderColorDirective,
        ToggleSwitchDirective,
    ],
})
export class SpeechToScoreInfoDialogComponent {
    public preferenceService: PreferenceService = inject(PreferenceService);
    public speechToScoreService: SpeechToScoreService = inject(SpeechToScoreService);

    private modalService: ModalService = inject(ModalService);
    private janusStateService: JanusStateService = inject(JanusStateService);
    private alertService: AlertService = inject(AlertService);

    public editableType: WritableSignal<CommandType | null> = signal(null);

    public toggleSpeechToScore(): void {
        this.preferenceService.enableSpeechToScore.set(!this.preferenceService.enableSpeechToScore());

        if (this.preferenceService.enableSpeechToScore()) {
            if (this.janusStateService.audioPublisher().isPublishing) {
                this.preferenceService.enableSpeechToScore.set(false);
                this.alertService.createAlert({
                    title: $localize`:@@CANNOT_USE_SPEECH_TO_SCORE_WITH_VOICE_CALL:Cannot use Speech to score with Voice chat`,
                    text: $localize`:@@PLEASE_DISABLE_VOICE_CALL_TO_USE_SPEECH_TO_SCORE:Please disable Voice chat in order to use Speech to score.`,
                    icon: 'info',
                });
                return;
            }

            this.preferenceService.savePreferences();
            this.speechToScoreService.start();
        } else {
            this.preferenceService.savePreferences();
            this.speechToScoreService.stop();
        }
    }

    public changeType(type: CommandType): void {
        if (this.editableType()) {
            this.editableType.set(null);
        } else {
            this.editableType.set(type);
        }
    }

    public changeOption(option: CommandOption | RecognitionLang): void {
        this.speechToScoreService.changeOption(this.editableType(), option);
        this.editableType.set(null);
    }

    public dismiss(returnValue: any = null): void {
        this.modalService.dismiss(returnValue);
    }
}
